import type { ComposedForm } from 'lib-node-question-builder';
import { or, screenIs } from 'lib-node-question-builder/utils';

import type { ProductType } from '~/common/hooks/useGetLoanSummary';
import { READONLY_CONDITION } from '~/pages/Loan1003DataGrids/common/utils/utils';

import type { BorrowerData, BorrowerDropdownOptions } from '../../../types';

const TODAY = new Date().toString();

type BorrowerInformationGridProps = {
  form: ComposedForm<
    BorrowerData['formData'],
    {
      isVA: boolean;
      productType: ProductType;
      refiPurpose?: string | null;
    }
  >;
  dropdownOptions: BorrowerDropdownOptions;
};

const createMaxLengthRule = (maxLength: number, message: string) => ({
  type: 'string' as const,
  maxLength,
  message,
});

const createRequiredMaxLengthRule = (maxLength: number, message: string) => ({
  type: 'and' as const,
  rules: [
    {
      type: 'required' as const,
      message: 'Required',
    },
    createMaxLengthRule(maxLength, message),
  ],
});

export function BorrowerInformationGrid({
  form,
  dropdownOptions,
}: Readonly<BorrowerInformationGridProps>) {
  return (
    <form.Grid
      name="borrower information grid"
      columns={6}
      style={{ padding: '0 0.5rem' }}
    >
      <form.GridItem>
        <form.Text
          name="firstName"
          label="First Name"
          placeholder="First Name"
          rules={{
            required: true,
            onBlur: createRequiredMaxLengthRule(
              30,
              'First name cannot exceed 30 characters',
            ),
          }}
          conditions={{
            isDisabled: READONLY_CONDITION,
          }}
        />
      </form.GridItem>
      <form.GridItem>
        <form.Text
          name="middleName"
          label="Middle Name"
          placeholder="Middle Name"
          rules={{
            onBlur: createMaxLengthRule(
              30,
              'Middle name cannot exceed 30 characters',
            ),
          }}
          conditions={{
            isDisabled: READONLY_CONDITION,
          }}
        />
      </form.GridItem>
      <form.GridItem>
        <form.Text
          name="lastName"
          label="Last Name"
          placeholder="Last Name"
          rules={{
            required: true,
            onBlur: createRequiredMaxLengthRule(
              80,
              'Last name cannot exceed 80 characters',
            ),
          }}
          conditions={{
            isDisabled: READONLY_CONDITION,
          }}
        />
      </form.GridItem>
      <form.GridItem>
        <form.Text
          name="suffixName"
          label="Suffix"
          rules={{
            onBlur: createMaxLengthRule(
              20,
              'Suffix cannot exceed 20 characters',
            ),
          }}
          conditions={{
            isDisabled: READONLY_CONDITION,
          }}
        />
      </form.GridItem>
      <form.GridItem>
        <form.Select
          name="borrowerPosition"
          label="Borrower Position"
          placeholder="Select Option"
          options={dropdownOptions.borrowerPosition}
          rules={{
            required: true,
          }}
          conditions={{
            isDisabled: READONLY_CONDITION,
          }}
        />
      </form.GridItem>
      <form.GridItem>
        <form.Text
          name="caivrsNumber"
          label="CAIVRS"
          placeholder="Enter CAIVRS Number"
          conditions={{
            isVisible: or(
              screenIs('productType', 'FHA'),
              screenIs('refiPurpose', 'VA Interest Rate Reduction'),
            ),
            isDisabled: READONLY_CONDITION,
          }}
        />
      </form.GridItem>
      <form.GridItem>
        <form.Masked
          name="socialSecurityNumber"
          label="SSN"
          pattern="###-##-####"
          placeholder="123-45-6789"
          rules={{
            required: true,
            onChange: {
              type: 'string',
              pattern: String.raw`^\d{3}-\d{2}-\d{4}$`,
              message: 'SSN must be in the format 999-99-9999',
            },
          }}
          conditions={{
            isDisabled: READONLY_CONDITION,
          }}
        />
      </form.GridItem>
      <form.GridItem>
        <form.Date
          name="dateOfBirth"
          label="Date of Birth"
          rules={{
            required: true,
            onChange: {
              type: 'and',
              rules: [
                {
                  type: 'date',
                  before: TODAY,
                  message: 'Date of Birth cannot be after today',
                },
                {
                  type: 'date',
                  after: '1910-01-01T00:00:00',
                  message: 'Date of Birth cannot be before 1/1/1910',
                },
              ],
            },
          }}
          conditions={{
            isDisabled: READONLY_CONDITION,
          }}
        />
      </form.GridItem>
      <form.GridItem>
        <form.Number
          name="age"
          label="Age"
          conditions={{
            isDisabled: true,
          }}
        />
      </form.GridItem>
      <form.GridItem>
        <div />
      </form.GridItem>
      <form.GridItem colSpan={2}>
        <form.Text
          name="emailAddress"
          label="Email Address"
          placeholder="Email Address"
          rules={{
            onBlur: {
              type: 'string',
              pattern: String.raw`^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$`,
              message: 'Invalid email',
            },
          }}
          conditions={{
            isDisabled: READONLY_CONDITION,
          }}
        />
      </form.GridItem>
      <form.Subscribe selector={(state) => state.values.isNameOrSsnChanged}>
        {(isNameOrSsnChanged) => {
          if (isNameOrSsnChanged) {
            return (
              <form.GridItem colSpan={4}>
                <form.Alert
                  name="ssnChangeAlert"
                  severity="warning"
                  message="Changing SSN or name may result in a detached credit report. Re-run AUS to determine if a new credit report is needed."
                  conditions={{
                    isDisabled: READONLY_CONDITION,
                    isVisible: {
                      when: 'refiPurpose',
                      dataSource: 'screenData',
                      isNot: 'VA Interest Rate Reduction',
                    },
                  }}
                />
              </form.GridItem>
            );
          }

          return (
            <form.GridItem colSpan={4}>
              <div />
            </form.GridItem>
          );
        }}
      </form.Subscribe>
      <form.GridItem colSpan={2}>
        <form.PhoneNumber
          name="cellPhone"
          label="Cell Phone"
          conditions={{
            isDisabled: READONLY_CONDITION,
          }}
        />
      </form.GridItem>
      <form.GridItem>
        <form.Select
          name="citizenship"
          label="Citizenship Status"
          placeholder="Select Option"
          rules={{
            required: 'Select a valid Citizenship Status',
          }}
          options={dropdownOptions.citizenship}
          conditions={{
            isDisabled: READONLY_CONDITION,
          }}
        />
      </form.GridItem>
      <form.GridItem>
        <form.Select
          name="maritalStatus"
          label="Marital Status"
          placeholder="Select Option"
          rules={{
            required: 'Select a valid Marital Status',
          }}
          options={dropdownOptions.maritalStatus}
          conditions={{
            isDisabled: READONLY_CONDITION,
          }}
        />
      </form.GridItem>
      <form.GridItem>
        <form.Number
          name="numberOfDependents"
          label={String.raw`\# of Dependents`}
          placeholder="Enter # of Dependents"
          conditions={{
            isDisabled: READONLY_CONDITION,
          }}
        />
      </form.GridItem>
      <form.GridItem>
        <form.Number
          name="numberOfFamilyMembers"
          label="Number of Family Members"
          placeholder="Enter Number of Family Members"
          min={0}
          step={1}
          allowDecimal={false}
          allowNegative={false}
          rules={{
            onSubmit: {
              type: 'and',
              rules: [
                {
                  type: 'number',
                  integer: true,
                  message: 'Must be a whole number of 0 or greater',
                },
                {
                  type: 'number',
                  gte: 0,
                  message: 'Must be a whole number of 0 or greater',
                },
              ],
            },
          }}
          conditions={{
            isVisible: screenIs('isVA', true),
            isDisabled: READONLY_CONDITION,
          }}
        />
      </form.GridItem>
      <form.GridItem>
        <form.Text
          name="agesOfDependents"
          label="Ages of Dependents"
          placeholder="Ages ex: 1, 4, 7"
          rules={{
            onBlur: {
              type: 'string',
              pattern: '^[0-9, ]*$',
              message: 'Invalid format',
            },
          }}
          conditions={{
            isDisabled: READONLY_CONDITION,
          }}
        />
      </form.GridItem>
      <form.GridItem colSpan={2}>
        <form.PhoneNumber
          name="homePhone"
          label="Home Phone"
          conditions={{
            isDisabled: READONLY_CONDITION,
          }}
        />
      </form.GridItem>
      <form.GridItem colSpan={2}>
        <form.Text
          name="primaryResidenceAddress"
          label="Primary Residence"
          conditions={{
            isDisabled: true,
          }}
        />
      </form.GridItem>
      <form.GridItem colSpan={2}>
        <form.Select
          name="joinedBorrower.borrowerRecordId"
          label="Joined to Borrower"
          placeholder="None"
          options={dropdownOptions.joinedBorrowers}
          conditions={{
            isDisabled: READONLY_CONDITION,
          }}
        />
      </form.GridItem>
      <form.GridItem colSpan={2}>
        <form.PhoneNumber
          name="businessPhone"
          label="Work Phone"
          conditions={{
            isDisabled: READONLY_CONDITION,
          }}
        />
      </form.GridItem>
      <form.Subscribe
        selector={(state) => state.values.isJoinedBorrowerChanged}
      >
        {(isJoinedBorrowerChanged) => {
          if (isJoinedBorrowerChanged) {
            return (
              <form.GridItem colSpan={4}>
                <form.Alert
                  name="joinedBorrowerAlert"
                  severity="warning"
                  message="Joining or unjoining a borrower will result in a detached credit report. Re-run AUS to determine if a new credit report is needed."
                  conditions={{
                    isDisabled: READONLY_CONDITION,
                  }}
                />
              </form.GridItem>
            );
          }

          return (
            <form.GridItem colSpan={4}>
              <div />
            </form.GridItem>
          );
        }}
      </form.Subscribe>
      <form.GridItem>
        <form.Text
          name="businessExtension"
          label="Ext."
          placeholder="123456"
          rules={{
            onChange: {
              type: 'and',
              rules: [
                {
                  type: 'string',
                  pattern: '^[0-9]*$',
                  message: 'Extension must contain only digits',
                },
                {
                  type: 'string',
                  maxLength: 6,
                  message: 'Extension cannot exceed 6 characters',
                },
              ],
            },
          }}
          conditions={{
            isDisabled: READONLY_CONDITION,
          }}
        />
      </form.GridItem>
      <form.GridItem colStart={1}>
        <form.Checkbox
          name="isVeteran"
          label="Veteran"
          conditions={{
            isVisible: screenIs('productType', 'VA'),
            isDisabled: READONLY_CONDITION,
          }}
        />
      </form.GridItem>
    </form.Grid>
  );
}
